import { Vector2, Vector2Like, Vector3Like } from 'three'
import { Ceiling, Wall, WallSegment } from '~/store/objects/types'
import { objectModelToUI, objectUIToModel } from '~/components/DrawingCanvas/util/units'
import LayerKeys from '~/config/layerKeys'
import polybool, { Polygon } from '@velipso/polybool'
import Util from '~/components/DrawingCanvas/lib/util'
import sample from 'lodash-es/sample'
import theme from '~/config/theme'
import { DEFAULT_CEILINGS_HEIGHT } from '~/config/ceilings'
import { wallIsClosed, wallIsValid, wallIsValidAndPolygon } from './walls'

function isPointInPolygon(point: Vector2Like, polygonPoints: Vector2Like[]) {
  // ray-casting algorithm based on
  // http://www.ecse.rpi.edu/Homepages/wrf/Research/Short_Notes/pnpoly.html
  const x = point.x
  const y = point.y

  let inside = false
  let i = 0
  for (let j = polygonPoints.length - 1; i < polygonPoints.length; j = i - 1) {
    const xi = polygonPoints[i].x
    const yi = polygonPoints[i].y
    const xj = polygonPoints[j].x
    const yj = polygonPoints[j].y

    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi
    if (intersect) inside = !inside

    i += 1
  }

  return inside
}

function arctanOf(point: Vector2Like, center: Vector2Like) {
  const diff = new Vector2().subVectors(point, center)
  const rcos = diff.dot(center)
  const rsin = diff.cross(center)
  return Math.atan2(rcos, rsin)
}

function clockwise(polygon: Vector2Like[]): Vector2Like[] {
  const centerishX = polygon.reduce((accumulator, current) => accumulator + current.x, 0) / polygon.length
  const centerishY = polygon.reduce((accumulator, current) => accumulator + current.y, 0) / polygon.length
  const centerish = { x: centerishX, y: centerishY }

  return new Array(...polygon).sort((a, b) => {
    return arctanOf(a, centerish) - arctanOf(b, centerish)
  })
}

function subdivideRegion(
  polygon: Vector2Like[],
  polygons: Vector3Like[][],
  polylines: Vector3Like[][]
): Vector2Like[][] {
  const containedPolygons = polygons.filter(it => it.every(pt => isPointInPolygon(pt, polygon)))

  const containedPolylines = polylines.flatMap((polyline): [Vector3Like[], number, number][] => {
    const first = polyline[0]
    const last = polyline[polyline.length - 1]

    let firstIdx: number | undefined = undefined
    let lastIdx: number | undefined = undefined

    for (let i = 0; i < polygon.length - 1; i++) {
      const a = polygon[i]
      const b = polygon[i + 1]

      const firstDist = Util.distanceToLineSegment(a.x, a.y, b.x, b.y, first.x, first.y)
      const lastDist = Util.distanceToLineSegment(a.x, a.y, b.x, b.y, last.x, last.y)

      if (firstDist < 0.05) {
        firstIdx = i
      }
      if (lastDist < 0.05) {
        lastIdx = i
      }
    }

    if (firstIdx !== undefined && lastIdx !== undefined) {
      return [[polyline, firstIdx, lastIdx]]
    }

    return []
  })

  if (containedPolylines.length > 0) {
    const polyline = containedPolylines[0][0]
    let spliceStart = containedPolylines[0][1]
    let spliceEnd = containedPolylines[0][2]
    if (spliceStart > spliceEnd) {
      ;[spliceStart, spliceEnd] = [spliceEnd, spliceStart]
    }

    const primaryPolygon = new Array(...polygon)
    const discardedPoints = primaryPolygon.splice(spliceStart + 1, spliceEnd - spliceStart, ...polyline)

    const head = polyline[0]
    const tail = polyline.slice(1)

    const secondaryPolygon = [head, ...discardedPoints, ...tail]
    primaryPolygon.pop()

    const sortedPrimary = clockwise(primaryPolygon)
    const sortedSecondary = clockwise(secondaryPolygon)

    return [
      [...sortedPrimary, sortedPrimary[0]],
      [...sortedSecondary, sortedSecondary[0]],
    ].flatMap(polygon =>
      subdivideRegion(
        polygon,
        polygons,
        polylines.filter(line => line !== polyline)
      )
    )
  }

  if (containedPolygons.length === 0) {
    return [polygon]
  }

  return containedPolygons
}

function computeRegions(segments: Record<string, WallSegment>, walls: Record<string, Wall>): Vector2Like[][] {
  const polygons = Object.values(walls)
    .filter(wall => wallIsValidAndPolygon(segments, wall))
    .filter(wall => wall.layerKey === LayerKeys.EXTERIOR_WALLS)
    .map(wall => {
      const points = wall.segments.map(segment => segments[segment].startPoint)
      points.push(segments[wall.segments[wall.segments.length - 1]].endPoint)
      return points
    })

  const allPolygons = Object.values(walls)
    .filter(wall => wallIsValidAndPolygon(segments, wall))
    .map(wall => {
      const points = wall.segments.map(segment => segments[segment].startPoint)
      points.push(segments[wall.segments[wall.segments.length - 1]].endPoint)
      return points
    })

  const allPolylines = Object.values(walls)
    .filter(wall => wallIsValid(segments, wall) && !wallIsClosed(segments, wall))
    .map(wall => {
      const points = wall.segments.map(segment => segments[segment].startPoint)
      points.push(segments[wall.segments[wall.segments.length - 1]].endPoint)
      return points
    })

  return polygons.flatMap(polygon => subdivideRegion(polygon, allPolygons, allPolylines))
}

function polygonSimilarity(a: Vector2Like[], b: Vector2Like[]) {
  const polyA: Polygon = {
    regions: [a.slice(0, a.length - 1).map(item => [item.x, item.y])],
    inverted: false,
  }
  const polyB: Polygon = {
    regions: [b.slice(0, b.length - 1).map(item => [item.x, item.y])],
    inverted: false,
  }
  const intersection = polybool.intersect(polyA, polyB)

  if (intersection.regions.length !== 1) {
    return 0
  }

  const areaA = Util.polygonArea(a)
  const areaB = Util.polygonArea(b)
  const intersectionArea = Util.polygonArea(intersection.regions[0].map(([x, y]) => ({ x, y })))

  let sim1 = intersectionArea / areaA
  sim1 = sim1 > 1 ? 1 / sim1 : sim1

  let sim2 = intersectionArea / areaB
  sim2 = sim2 > 1 ? 1 / sim2 : sim2

  return Math.min(sim1, sim2)
}

export function updateCeilings(
  segments: Record<string, WallSegment>,
  walls: Record<string, Wall>,
  ceilings_: Record<string, Ceiling>
) {
  const regions = computeRegions(segments, walls)
  const ceilings = Object.values(ceilings_)

  const ceilingsToDeleteCount = Math.max(ceilings.length - regions.length, 0)
  const ceilingsToAddCount = Math.max(regions.length - ceilings.length, 0)

  const ceilingsToRegions = new Map<Ceiling, Vector2Like[] | null>()
  const unmatchedRegions = new Set(regions)
  const unmatchedCeilings = new Set<Ceiling>()

  for (const ceiling of ceilings) {
    if (unmatchedRegions.size === 0) {
      ceilingsToRegions.set(ceiling, null)
      continue
    }

    const regionSimilarities = Array.from(unmatchedRegions.values()).map(region => ({
      region,
      similarity: polygonSimilarity(
        ceiling.perimeterPoints.map(pt => objectUIToModel(pt)),
        region
      ),
    }))

    regionSimilarities.sort((a, b) => b.similarity - a.similarity)
    const highestSimilarity = regionSimilarities[0].similarity

    if (highestSimilarity > 0) {
      const highestRegion = regionSimilarities[0].region

      ceilingsToRegions.set(ceiling, highestRegion)
      unmatchedRegions.delete(highestRegion)
    } else {
      ceilingsToRegions.set(ceiling, null)
      unmatchedCeilings.add(ceiling)
    }
  }

  if (unmatchedRegions.size === 1 && unmatchedCeilings.size === 1) {
    const region = Array.from(unmatchedRegions)[0]
    const ceiling = Array.from(unmatchedCeilings)[0]

    ceilingsToRegions.set(ceiling, region)
    unmatchedRegions.delete(region)
  }

  const ceilingsWithoutRegions = Array.from(ceilingsToRegions.entries())
    .filter(entry => entry[1] === null)
    .map(entry => entry[0])
  const ceilingsToDelete = ceilingsWithoutRegions

  const foundCorrectNumberToDelete = ceilingsToDeleteCount === ceilingsToDelete.length

  const foundCorrectNumberToAdd = ceilingsToAddCount === unmatchedRegions.size

  console.assert(foundCorrectNumberToDelete, `Error when updating ceilings: incorrect number of ceilings to delete`)
  console.assert(foundCorrectNumberToAdd, `Error when updating ceilings: incorrect number of ceilings to add`)

  return {
    ceilingsToDelete,
    ceilingsToAdd: Array.from(unmatchedRegions).map<Ceiling>(region => {
      return {
        id: Util.guid(),
        layerKey: LayerKeys.CEILINGS,
        enabled: false,
        color: sample(theme.colors.swatches) ?? theme.colors.swatches[0],
        perimeterPoints: region.map(objectModelToUI),
        height: DEFAULT_CEILINGS_HEIGHT,
      }
    }),
    ceilingsToUpdate: Array.from(ceilingsToRegions)
      .filter((args): args is [Ceiling, Vector2Like[]] => {
        const [ceiling, region] = args
        if (region === null) {
          return false
        }
        const similarity = polygonSimilarity(ceiling.perimeterPoints.map(objectUIToModel), region)
        return Math.abs(similarity - 1) > 0.005
      })
      .map(([ceiling, region]) => {
        return {
          ...ceiling,
          perimeterPoints: region.map(objectModelToUI),
        }
      }),
  }
}
